import { useState } from "react";
import { usePageBar } from "../contexts/PageBarContext";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useInvites } from "../hooks/useInvites";
import type { Connection } from "../types";

export function Connections() {
  const { user, userProfile } = useAuth();
  usePageBar({ title: "Connections", showTitle: true });
  const { invites, loading: invitesLoading } = useInvites();
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);

  if (!user || !userProfile) {
    return <p>Please sign in to manage connections.</p>;
  }

  const connections = Object.entries(userProfile.connections || {}) as [string, Connection][];
  const accepted = connections.filter(([, c]) => c.status === "accepted");
  const pending = connections.filter(([, c]) => c.status === "pending");

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addName.trim()) return;

    setSubmitting(true);
    setError(null);
    try {
      const createConnection = httpsCallable(functions, "createconnection");
      await createConnection({
        displayName: addName.trim(),
        ...(addEmail.trim() && { email: addEmail.trim() }),
      });
      setAddName("");
      setAddEmail("");
      setShowAdd(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to create connection";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (connectionId: string, displayName: string) => {
    if (!confirm(`Remove ${displayName} from your connections?`)) return;
    setDeletingId(connectionId);
    try {
      const deleteConnection = httpsCallable(functions, "deleteconnection");
      await deleteConnection({ connectionId });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to delete connection";
      setError(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleAcceptInvite = async (inviteId: string) => {
    setAcceptingId(inviteId);
    setError(null);
    try {
      const acceptInvite = httpsCallable(functions, "acceptinvite");
      await acceptInvite({ inviteId });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to accept invite";
      setError(message);
    } finally {
      setAcceptingId(null);
    }
  };

  const renderAvatar = (name: string, avatarUrl: string | null) => (
    <div className="party-avatar">
      {avatarUrl ? (
        <img src={avatarUrl} alt={name} />
      ) : (
        <span>{name.charAt(0).toUpperCase()}</span>
      )}
    </div>
  );

  return (
    <div className="page">
      <h1 className="page-head">Connections</h1>

      {error && <div className="form-error">{error}</div>}

      {/* Pending Invites */}
      {!invitesLoading && invites.length > 0 && (
        <section>
          <h2>Pending Invites</h2>
          <div className="connections-list">
            {invites.map((invite) => (
              <div key={invite.id} className="connection-item">
                <div className="party-avatar">
                  <span>{invite.fromDisplayName.charAt(0).toUpperCase()}</span>
                </div>
                <div className="connection-info">
                  <span className="party-name">{invite.fromDisplayName}</span>
                  <span className="text-muted">wants to connect</span>
                </div>
                <button
                  className="btn btn-small"
                  onClick={() => handleAcceptInvite(invite.id)}
                  disabled={acceptingId === invite.id}
                >
                  {acceptingId === invite.id ? "Accepting..." : "Accept"}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Connections */}
      <section>
        <div className="section-header">
          <h2>Your Connections ({connections.length})</h2>
          <button className="btn btn-small" onClick={() => setShowAdd(!showAdd)}>
            + Add Connection
          </button>
        </div>

        {showAdd && (
          <div className="add-form-wrapper">
            <form className="form" onSubmit={handleAdd}>
              <div className="form-field">
                <label className="form-label">Name</label>
                <input
                  className="form-input"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  placeholder="Display name"
                  required
                />
              </div>
              <div className="form-field">
                <label className="form-label">Email (optional)</label>
                <input
                  className="form-input"
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="If they have an account, they'll be linked automatically"
                />
              </div>
              <div className="form-actions">
                <button className="btn" type="submit" disabled={submitting}>
                  {submitting ? "Adding..." : "Add"}
                </button>
                <button className="btn btn-secondary" type="button" onClick={() => setShowAdd(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {accepted.length > 0 && (
          <div className="connections-list">
            {accepted.map(([id, conn]) => (
              <div key={id} className="connection-item">
                {renderAvatar(conn.displayName, conn.avatarUrl)}
                <div className="connection-info">
                  <span className="party-name">{conn.displayName}</span>
                  {conn.email && <span className="text-muted">{conn.email}</span>}
                </div>
                <button
                  className="btn btn-small btn-danger"
                  onClick={() => handleDelete(id, conn.displayName)}
                  disabled={deletingId === id}
                >
                  {deletingId === id ? "Removing..." : "Remove"}
                </button>
              </div>
            ))}
          </div>
        )}

        {pending.length > 0 && (
          <>
            <h3 className="connections-subheading">Pending</h3>
            <div className="connections-list">
              {pending.map(([id, conn]) => (
                <div key={id} className="connection-item connection-pending">
                  {renderAvatar(conn.displayName, conn.avatarUrl)}
                  <div className="connection-info">
                    <span className="party-name">{conn.displayName}</span>
                    {conn.email && <span className="text-muted">{conn.email}</span>}
                    <span className="status-badge status-pending">Pending</span>
                  </div>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(id, conn.displayName)}
                    disabled={deletingId === id}
                  >
                    {deletingId === id ? "Removing..." : "Remove"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {connections.length === 0 && !showAdd && (
          <p className="text-muted">No connections yet. Add someone to start building your travel party!</p>
        )}
      </section>
    </div>
  );
}
